import { css } from '@emotion/react';
import {
  standardSpacing,
  largeSpacing,
  quarterSpacing,
  halfSpacing,
  threeQuartersSpacing,
  lectureTopPanelZIndex,
} from 'styles/global_defaults/scaffolding';
import {
  gray4,
  primary,
  black,
  gray2,
  genAIGradientType3,
  white,
  genAIGradientType2,
  genAIGradient,
  gray3,
  warning,
  lightSoftYellow,
} from 'styles/global_defaults/colors';
import {
  textMediumFontSize,
  textSmallFontSize,
  boldFontWeight,
  textLargeFontSize,
  semiBoldFontWeight,
  lightFontWeight,
  headerFontSize,
  headerLineHeight,
  textXSFontSize,
  normalFontWeight,
} from 'styles/global_defaults/fonts';

// CSS Variables
export const askNovoAiBreakpoint = 520;

export const questionTextStyles = css`
  font-size: ${textLargeFontSize}px;
  font-weight: ${boldFontWeight};
  margin-bottom: ${largeSpacing}px;
  text-align: center;
`;

export const showMoreLessButtonStyles = css`
  background: none;
  border: none;
  color: ${primary};
  font-size: ${textSmallFontSize}px;
  font-weight: ${normalFontWeight};
  padding: 0;
  margin-left: ${quarterSpacing}px;
  margin-top: 0 !important;

  &:hover {
    text-decoration: underline;
  }
`;

export const askNovoAiContainer = (isLecturePage: boolean, expanded: boolean) => css`
  max-height: 88vh;
  overflow-x: hidden;
  overflow: auto;
  position: fixed;
  right: 0;
  bottom: ${isLecturePage ? '70px' : 0};
  z-index: ${lectureTopPanelZIndex};
  border-radius: ${halfSpacing}px ${halfSpacing}px 0 0;
  background: ${white};
  transform: translate3d(0, 0, 0); /* Safari hardware acceleration fix */
  isolation: isolate; /* Creates new stacking context for Safari */
  -webkit-transform: translateZ(0); /* Force Safari to create compositing layer */
  will-change: transform; /* Hint to browser for optimization */

  width: ${expanded ? '100%' : 'inherit'};
  @media screen and (min-width: ${askNovoAiBreakpoint}px) {
    width: inherit;
  }

  .icon-gradient {
    color: rgba(0, 204, 188, 1);
  }
`;

export const novoAiExpandedReadOnlyWrapper = css`
  .icon-gradient {
    color: rgba(0, 204, 188, 1);
  }
  pointer-events: none;
  min-height: 230px;
  width: 360px;
  border-radius: ${threeQuartersSpacing}px ${threeQuartersSpacing}px 0 0;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  padding: ${standardSpacing}px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: ${genAIGradientType2};

  .novoai-drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: ${halfSpacing}px;
  }
  .novoai-header-left {
    display: flex;
    gap: ${quarterSpacing}px;
    align-items: center;
  }

  .novoai-icon {
    margin-right: ${halfSpacing}px;
  }

  .novoai-title {
    font-weight: ${boldFontWeight};
    color: #00ccbc;
  }

  .novoai-drawer-close {
    background: none;
    border: none;
    font-size: ${textLargeFontSize}px;
    color: ${gray4};
    cursor: pointer;
    padding: ${quarterSpacing}px;
  }

  .novoai-drawer-content {
    display: flex;
    flex-direction: column;
    gap: ${halfSpacing}px;
    align-items: center;
    justify-content: center;
  }

  .novoai-greeting {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${quarterSpacing}px;
    font-size: ${textLargeFontSize}px;
    font-weight: ${semiBoldFontWeight};
    color: ${black};
  }

  .novoai-wave {
    font-size: ${textLargeFontSize}px;
  }
  .novoai-greeting-text {
    font-weight: ${lightFontWeight};
  }
  .novoai-description {
    font-size: ${textSmallFontSize}px;
    color: ${gray2};
    margin-bottom: ${halfSpacing}px;
    text-align: center;
  }
  .novoai-input-wrapper {
    width: 100%;
  }
  .novoai-input {
    border: 1px solid #00ccbc;
    background: transparent;
    font-size: ${textSmallFontSize}px;
    min-height: 50px;
    border-radius: ${halfSpacing}px;
    transition: border 0.2s;
  }
  .novoai-input:focus {
    border: 1.5px solid ${primary};
  }
`;

export const novoAiExpandedWrapper = css`
  min-height: 320px;
  width: 100%;
  @media screen and (min-width: ${askNovoAiBreakpoint}px) {
    width: ${askNovoAiBreakpoint}px;
  }
  border-radius: ${threeQuartersSpacing}px ${threeQuartersSpacing}px 0 0;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  animation: novoai-drawer-in 0.2s;
  background: ${genAIGradientType2};

  .loading-icon {
    position: relative;
    margin: 0 0 ${standardSpacing}px ${halfSpacing}px;

    .text-container {
      margin-top: 0 !important;
    }
    p {
      display: none;
    }
  }

  .lds-grid {
    position: unset !important;
  }

  @keyframes novoai-drawer-in {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
  .novoai-drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 ${standardSpacing}px;
    height: 60px;
    position: sticky;
    top: 0;
    background: #f2fbfc;
  }
  .novoai-header-left {
    display: flex;
    gap: ${quarterSpacing}px;
    align-items: center;
  }

  .novoai-icon {
    margin-right: ${halfSpacing}px;
  }

  .novoai-title {
    font-weight: ${boldFontWeight};
    color: ${black};
  }

  .novoai-drawer-close {
    background: none;
    border: none;
    font-size: ${textLargeFontSize}px;
    color: ${gray4};
    cursor: pointer;
    padding: ${quarterSpacing}px;
    transition: background 0.2s;
  }

  .novoai-drawer-content {
    display: flex;
    flex-direction: column;
    gap: ${standardSpacing}px;
    padding: ${standardSpacing}px;
    align-items: center;
    justify-content: center;
  }

  .novoai-greeting {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${halfSpacing}px;
    font-size: ${textLargeFontSize}px;
    font-weight: ${semiBoldFontWeight};
    color: ${black};
  }

  .novoai-wave {
    font-size: ${textLargeFontSize}px;
  }
  .novoai-greeting-text {
    font-weight: ${lightFontWeight};
  }
  .novoai-description {
    font-size: ${textMediumFontSize}px;
    color: ${black};
    margin-bottom: ${halfSpacing}px;
    text-align: center;
  }
  .novoai-input-wrapper {
    width: 100%;
  }
  .novoai-input {
    border: 1px solid #00ccbc;
    background: transparent;
    min-height: 80px;
    border-radius: ${halfSpacing}px;
    transition: border 0.2s;
    max-height: 330px;
  }
  .novoai-input:focus {
    border: 1.5px solid ${primary};
  }

  .recent-questions-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${halfSpacing}px;

    .recent-questions-header {
      font-weight: ${semiBoldFontWeight};
      margin-bottom: 0;
    }

    .recent-questions-wrapper {
      display: flex;
      align-items: center;
      gap: ${quarterSpacing}px;
      padding: ${quarterSpacing}px ${halfSpacing}px;
      border-radius: ${largeSpacing}px;
      border: none;
      background-color: #ddf9f5;
      width: auto;
      @media screen and (min-width: ${askNovoAiBreakpoint}px) {
        width: fit-content;
      }

      .icon {
        background: ${genAIGradient};
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }

      .recent-questions-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 440px;
      }
    }
  }

  .processing-upload {
    border-top: 1px solid ${gray4};
    padding-top: ${halfSpacing}px;
    color: ${gray3};
    margin-right: ${quarterSpacing}px;
  }
`;

// Collapsed button styles
export const novoAiCollapsedWrapper = css`
  border: none;
  border-radius: ${halfSpacing}px ${halfSpacing}px 0 0;
  box-shadow: 0px ${halfSpacing}px ${halfSpacing}px 0px rgba(29, 33, 38, 0.1);
  padding: 0 ${standardSpacing}px;
  cursor: pointer;
  width: 200px;
  height: 65px;
  transition: box-shadow 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${genAIGradientType3};

  .novoai-collapsed-icon {
    display: flex;
    align-items: center;
    gap: ${quarterSpacing}px;
  }

  .novoai-collapsed-label {
    font-weight: ${boldFontWeight};
    color: ${black};
  }

  .novoai-collapsed-arrow {
    font-size: ${textSmallFontSize}px;
    color: ${primary};
    margin-left: ${quarterSpacing}px;
  }
`;

export const askNovoAIBtnComponent = (disabled?: boolean, isSmall?: boolean) => css`
  justify-content: center;
  display: flex;
  margin-top: ${threeQuartersSpacing}px;

  .gradient-border {
    display: inline-block;
    padding: 2px;
    border-radius: 25px;
    background: ${disabled ? gray4 : genAIGradient};
    width: ${isSmall ? 104 : 180}px;
  }

  .novoai-button {
    width: ${isSmall ? 100 : 176}px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${isSmall ? quarterSpacing : halfSpacing}px ${threeQuartersSpacing}px;
    font-size: ${isSmall ? textXSFontSize : textMediumFontSize}px;
    font-weight: ${boldFontWeight};
    border: none;
    border-radius: 25px;
    color: ${disabled ? gray4 : black};
    background: #f3fdfb;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);

    .icon-gen-ai {
      background: ${disabled ? gray4 : genAIGradient};
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }
`;

export const loadingDrawerState = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  .loading-icon {
    position: relative;
    margin: 0 0 ${standardSpacing}px ${halfSpacing}px;
  }

  .light {
    font-size: ${headerFontSize}px;
    margin-bottom: ${halfSpacing}px;
  }

  .lds-grid {
    position: unset !important;
  }

  .generating {
    margin: ${halfSpacing}px 0 ${largeSpacing}px ${quarterSpacing}px;
    height: 60px;
    width: 60px;
    color: #00ccbc;
  }

  button {
    background: none;
    border: none;
    color: ${primary};
    font-size: ${textMediumFontSize}px;
    cursor: pointer;
    margin-top: ${halfSpacing}px;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const responseDrawerState = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  .response-container {
    width: 100%;
  }
  .light {
    font-size: ${headerFontSize}px;
    margin-bottom: ${halfSpacing}px;
  }
  .divider {
    height: 1px;
    width: 100%;
    background: ${gray4};
    margin: ${threeQuartersSpacing}px 0;
  }

  .links-container {
    margin: ${halfSpacing}px 0;
  }

  .link-wrapper {
    display: flex;
    margin: ${halfSpacing}px 0;
    align-items: center;

    a {
      margin-left: ${quarterSpacing}px;
    }
    button {
      text-align: left;
    }
  }

  .answer {
    line-height: ${headerLineHeight}px;
  }

  .inline-code {
    font-size: ${textSmallFontSize}px;
    /* Apply to the first div only */
    > div:first-child {
      padding: 0 !important;
    }
    p {
      margin: 0;
    }
    a {
      background: none;
      border: none;
      color: ${primary};
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .link {
    background: none;
    border: none;
    color: ${primary};
    font-size: ${textMediumFontSize}px;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const helpfulnessStyles = css`
  display: flex;
  flex-direction: column;
  position: relative;
  margin-bottom: ${halfSpacing}px;

  .save-toast {
    position: absolute;
    top: -${standardSpacing}px;
    left: 45%;

    /* Add only to the first div child here */
    > div:first-child {
      background-color: ${lightSoftYellow};
    }
  }

  .helpfulness-container {
    display: flex;
    align-items: center;
    gap: ${halfSpacing}px;
    padding: ${halfSpacing}px 0;
  }

  .helpfulness-text {
    color: ${gray3};
    margin-right: ${quarterSpacing}px;
  }

  .vote-buttons {
    display: flex;
    gap: ${quarterSpacing}px;
  }

  .vote-button {
    background: none;
    border: none;
    padding: ${quarterSpacing}px;
    cursor: pointer;
    border-radius: ${quarterSpacing}px;
    display: flex;
    align-items: center;
    transition: all 0.2s ease;

    &.down {
      transform: rotate(180deg);
    }

    &:hover {
      background-color: ${white};
    }

    .voted {
      color: ${warning};
    }

    &.disabled {
      cursor: not-allowed;
      opacity: 0.5;

      &:hover {
        background-color: transparent;
      }
    }
  }

  .vote-icon {
    color: ${gray4};
  }

  .voted .vote-icon {
    color: ${white};
  }

  .feedback-text {
    font-size: ${textSmallFontSize}px;
    color: ${gray4};
    margin-left: ${halfSpacing}px;
  }
`;
